import { useEffect, useState } from "react"
import * as S from "./styled"
import { Dialog, DialogContent } from "@mui/material"
import { Input } from "../../../pages/Login/styled"
import { formatPhone } from "../../../utils/masks/phone"
import { onlyDigits } from "../../../utils/tb/phone/onlyDigits"

import { ReactComponent as WhatsappIcon } from "../../../assets/icons/whatsapp.svg"
import { ReactComponent as CheckCircle } from "../../../assets/icons/check_circle.svg"
import { ReactComponent as AttentionIcon } from "../../../assets/icons/attention.svg"

type Props = {
  shown: boolean
  basePhone: string
  handleConfirm: (code: string) => Promise<boolean>
  handleClose: () => void
  sentPhones?: string[]
  /** Mensagem de erro do último envio (quem usa o modal controla e limpa). */
  error?: string
  /** Textos opcionais — o padrão é o de "enviar PDF por WhatsApp". */
  copy?: {
    title?: string
    subtitle?: string
    helper?: string
    confirmLabel?: string
    sendingLabel?: string
    successTitle?: string
    successSubtext?: string
    resendTitle?: string
    resendSubtitle?: string
  }
}

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6 6 18" strokeLinecap="round" />
    <path d="m6 6 12 12" strokeLinecap="round" />
  </svg>
)

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path
      d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const AskPhoneNumberModal = ({
  shown,
  basePhone,
  handleConfirm,
  handleClose,
  sentPhones = [],
  error = "",
  copy = {},
}: Props) => {
  const [phone, setPhone] = useState(formatPhone(onlyDigits(basePhone)))
  const [canSend, setCanSend] = useState(true)
  const [sending, setSending] = useState(false)
  const [success, setSuccess] = useState(false)
  const [askingResend, setAskingResend] = useState(false)

  const doSend = async () => {
    let finalPhone = phone
    setAskingResend(false)
    setSending(true)
    const result = await handleConfirm(finalPhone)
    setSending(false)

    if (result) {
      setSuccess(true)
      setPhone("")
      // Fecha o modal após 2 segundos
      setTimeout(() => {
        setSuccess(false)
        handleClose()
      }, 2000)
    }
  }

  const onConfirm = () => {
    const alreadySent = sentPhones.map(onlyDigits).includes(onlyDigits(phone))

    if (alreadySent) setAskingResend(true)
    else doSend()
  }

  useEffect(() => {
    if (shown) {
      setPhone(formatPhone(onlyDigits(basePhone)))
    } else {
      setSuccess(false)
      setAskingResend(false)
      setPhone(formatPhone(onlyDigits(basePhone)))
    }
  }, [shown, basePhone])

  useEffect(() => {
    setCanSend(phone.replace(/\D/g, "").length === 11)
  }, [phone])

  return (
    <Dialog
      open={shown}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ style: { borderRadius: 20, position: "relative" } }}
    >
      <S.CloseButton type="button" onClick={handleClose} aria-label="Fechar">
        <CloseIcon />
      </S.CloseButton>

      <DialogContent style={{ padding: "28px 28px 24px" }}>
        {success ? (
          <S.SuccessWrapper>
            <S.SuccessIcon>
              <CheckCircle aria-hidden="true" />
            </S.SuccessIcon>
            <S.SuccessMessage>
              {copy.successTitle ?? "Ingressos enviados!"}
            </S.SuccessMessage>
            <S.SuccessSubtext>
              {copy.successSubtext ?? "Confira o WhatsApp em instantes."}
            </S.SuccessSubtext>
          </S.SuccessWrapper>
        ) : askingResend ? (
          <>
            <S.Header>
              <S.WarningIconBadge>
                <AttentionIcon aria-hidden="true" />
              </S.WarningIconBadge>
              <S.Title>{copy.resendTitle ?? "Enviar novamente?"}</S.Title>
              <S.Subtitle>
                {copy.resendSubtitle ?? (
                  <>
                    Os ingressos já foram enviados para o número{" "}
                    <strong>{phone}</strong>. Deseja enviá-los novamente para o
                    mesmo número?
                  </>
                )}
              </S.Subtitle>
            </S.Header>

            <S.Actions $vertical={true}>
              <S.Button type="button" $disabled={false} onClick={doSend}>
                <WhatsappIcon aria-hidden="true" />
                Enviar mesmo assim
              </S.Button>
              <S.CancelButton
                type="button"
                onClick={() => setAskingResend(false)}
              >
                Voltar
              </S.CancelButton>
            </S.Actions>
          </>
        ) : (
          <>
            <S.Header>
              <S.IconBadge>
                <WhatsappIcon aria-hidden="true" />
              </S.IconBadge>
              <S.Title>{copy.title ?? "Enviar ingressos por WhatsApp"}</S.Title>
              <S.Subtitle>
                {copy.subtitle ??
                  "Vamos enviar o PDF dos seus ingressos para o número abaixo."}
              </S.Subtitle>
            </S.Header>

            <S.Field>
              <S.Label htmlFor="whatsapp-phone">Número do WhatsApp</S.Label>
              <S.InputWrapper>
                <S.InputIcon>
                  <PhoneIcon />
                </S.InputIcon>
                <Input
                  id="whatsapp-phone"
                  style={{ paddingLeft: 44 }}
                  inputMode={"numeric"}
                  placeholder="(00) 00000-0000"
                  value={phone}
                  onChange={({ target }) =>
                    setPhone(formatPhone(onlyDigits(target.value)).slice(0, 13))
                  }
                />
              </S.InputWrapper>
              {(copy.helper ?? "Pode ser o número de outra pessoa, se preferir.") !==
                "" && (
                <S.HelperText>
                  {copy.helper ??
                    "Pode ser o número de outra pessoa, se preferir."}
                </S.HelperText>
              )}
              {error !== "" && <S.ErrorText role="alert">{error}</S.ErrorText>}
            </S.Field>

            <S.Actions>
              <S.Button
                type="button"
                $disabled={!canSend || sending}
                onClick={!canSend || sending ? undefined : onConfirm}
              >
                {sending ? (
                  <>
                    <S.Spinner />
                    {copy.sendingLabel ?? "Enviando..."}
                  </>
                ) : (
                  <>
                    <WhatsappIcon aria-hidden="true" />
                    {copy.confirmLabel ?? "Enviar"}
                  </>
                )}
              </S.Button>
              <S.CancelButton type="button" onClick={handleClose}>
                Cancelar
              </S.CancelButton>
            </S.Actions>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default AskPhoneNumberModal
